import * as React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { TreasuryTone } from "./treasury-data";

const TONE_CLASSES: Record<TreasuryTone, string> = {
  success: "bg-success-soft text-success border-success-border",
  warning: "bg-warning-soft text-warning border-warning-border",
  danger: "bg-danger-soft text-danger border-danger-border",
  info: "bg-info-soft text-info border-info-border",
  pending: "bg-pending-soft text-pending border-line",
  neutral: "bg-disabled-soft text-disabled border-line",
};

export function Modal({ open, onOpenChange, title, desc, children }: { open: boolean; onOpenChange: (v: boolean) => void; title: string; desc?: string; children: React.ReactNode }) {
  if (!open) return null;
  return (
    <div className="modal-overlay visible" style={{ display: "flex", position: "fixed", inset: 0, background: "var(--ads-overlay)", zIndex: 100, alignItems: "center", justifyContent: "center" }} onClick={() => onOpenChange(false)}>
      <div className="modal" style={{ background: "var(--ads-color-on-primary)", borderRadius: 14, padding: 28, width: "100%", maxWidth: 460, boxShadow: "var(--ads-shadow-modal)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div className="modal-title" style={{ fontSize: 17, fontWeight: 800, color: "var(--ads-color-text-strong)", marginBottom: 4 }}>{title}</div>
            {desc && <div className="modal-desc" style={{ fontSize: "var(--ads-type-sm)", color: "var(--ads-color-muted)", marginBottom: 20, lineHeight: 1.5 }}>{desc}</div>}
          </div>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} aria-label="Close dialog"><X className="size-4" /></Button>
        </div>
        {children}
      </div>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function TreasuryBanner({ label }: { label: string }) {
  return null;
}

export function BackLink({ href, children }: { href: string; children?: React.ReactNode }) {
  return (
    <Link to={href} className="back-link">
      <ArrowLeft aria-hidden className="size-4" /> {children ?? "← Back to Treasury"}
    </Link>
  );
}

export function ToneBadge({ tone, children }: { tone: TreasuryTone; children: React.ReactNode }) {
  return <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-bold whitespace-nowrap border", TONE_CLASSES[tone])}>{children}</span>;
}

export function PositionStatus({ tone, children }: { tone: TreasuryTone; children: React.ReactNode }) {
  const map: Record<TreasuryTone, string> = {
    success: "bg-success-soft text-success border-success-border",
    warning: "bg-warning-soft text-warning border-warning-border",
    danger: "bg-danger-soft text-danger border-danger-border",
    info: "bg-info-soft text-info border-info-border",
    pending: "bg-pending-soft text-pending border-line",
    neutral: "bg-disabled-soft text-disabled border-line",
  };
  return <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-bold border", map[tone])}>{children}</span>;
}

export function VenueBadge({ tone, children }: { tone: TreasuryTone; children: React.ReactNode }) {
  const map: Record<TreasuryTone, string> = {
    success: "bg-success-soft text-success border-success-border",
    warning: "bg-warning-soft text-warning border-warning-border",
    danger: "bg-danger-soft text-danger border-danger-border",
    info: "bg-info-soft text-info border-info-border",
    pending: "bg-pending-soft text-pending border-line",
    neutral: "bg-disabled-soft text-disabled border-line",
  };
  return <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold border", map[tone])}>{children}</span>;
}

export function InfoBanner({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <div style={{ background: "var(--ads-status-info-soft)", border: "1px solid var(--ads-status-info-border)", borderRadius: 10, padding: "12px 16px", display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 24 }}>
      <span style={{ fontSize: 16, flexShrink: 0 }}>ℹ</span>
      <span className="info-text" style={{ fontSize: "var(--ads-type-sm)", color: "var(--ads-status-info)", fontWeight: 500, lineHeight: 1.5 }}>{title && <strong>{title} </strong>}{children}</span>
    </div>
  );
}
